import React, { useEffect, useState } from 'react';
import AdminColHeader from '../../../components/AdminColHeader';
import { cn } from '../../../lib/utils';
import { SettingsPanel } from './SettingsPanel';
import { SettlementHistoryTimeline } from './SettlementHistoryTimeline';
import { useSettlementHistory } from './useSettlementHistory';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { BankCodeModal } from '../payoutmanagement/BankCodeModal';

export function BankCodeSettings() {
  const { showToast } = useToast();
  const [showBankModal, setShowBankModal] = useState(false);
  const [bankCodesAll, setBankCodesAll] = useState([]);
  const [bankCodeLoading, setBankCodeLoading] = useState(false);

  const reloadBankCodes = async () => {
    const r = await apiClient.get('/api/admin/payout/bank-codes/all');
    if (r.data?.success) setBankCodesAll(r.data.data || []);
  };

  const openBankCodeModal = async () => {
    setShowBankModal(true);
    setBankCodeLoading(true);
    try {
      await reloadBankCodes();
    } catch {
      showToast('은행코드를 불러오지 못했습니다.', 'error');
    } finally {
      setBankCodeLoading(false);
    }
  };

  const addBankCode = async (code, name) => {
    try {
      const res = await apiClient.post('/api/admin/payout/bank-codes', { code, name });
      if (res.data?.success) {
        showToast('은행코드가 추가되었습니다.', 'success');
        await reloadBankCodes();
      } else {
        showToast(res.data?.message || '추가 실패', 'error');
      }
    } catch {
      showToast('추가 실패', 'error');
    }
  };

  const toggleBankCode = async (id, isActive) => {
    try {
      if (isActive) {
        await apiClient.delete(`/api/admin/payout/bank-codes/${id}`);
      } else {
        const bank = bankCodesAll.find((item) => item.id === id);
        await apiClient.post('/api/admin/payout/bank-codes', { code: bank?.code, name: bank?.name });
      }
      await reloadBankCodes();
    } catch {
      showToast('상태 변경 실패', 'error');
    }
  };

  return (
    <>
      <SettingsPanel
        title="은행코드 관리"
        subtitle="정산지급과 급여이체에서 함께 사용하는 은행 코드입니다."
        right={<button onClick={openBankCodeModal} className="h-9 rounded-lg bg-slate-900 px-4 text-[12px] font-black text-white hover:bg-slate-800 dark:bg-accent-600 dark:hover:bg-accent-500">은행코드 열기</button>}
      >
        <div className="p-4 text-[12px] font-semibold text-[var(--text-muted)]">
          은행 검색 자동완성과 이체 파일 생성에서 사용하는 공통 코드입니다. 삭제 대신 비활성 상태로 관리됩니다.
        </div>
      </SettingsPanel>
      {showBankModal && (
        <BankCodeModal
          bankCodesAll={bankCodesAll}
          loading={bankCodeLoading}
          onAdd={addBankCode}
          onToggle={toggleBankCode}
          onClose={() => setShowBankModal(false)}
        />
      )}
    </>
  );
}

export function SettlementSettings({ settlementCompanies, settlementLoading }) {
  const {
    historyMap,
    historyLoading,
    saving: historySaving,
    fetchHistory,
    saveHistoryEntry,
    deleteHistoryEntry,
  } = useSettlementHistory();
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    if (!settlementCompanies.length) return;
    const first = settlementCompanies[0].code;
    if (!activeTab) {
      setActiveTab(first);
      fetchHistory(first);
    }
  }, [activeTab, fetchHistory, settlementCompanies]);

  function handleTabChange(code) {
    setActiveTab(code);
    if (!historyMap[code]) fetchHistory(code);
  }

  return (
    <SettingsPanel
      title={<AdminColHeader label="정산/매출 반영 설정 이력" db="settlement_settings" />}
      subtitle="카드승인·전화승인·입금 반영률, 상품 원가, 판매가, 배분율을 날짜별로 관리합니다."
    >
      {settlementLoading && (
        <div className="p-4 text-[12px] font-semibold text-[var(--text-muted)]">정산 설정을 불러오는 중입니다.</div>
      )}
      {!settlementLoading && settlementCompanies.length > 0 && (
        <>
          <div className="flex gap-1.5 overflow-x-auto border-b border-[var(--border)] p-2">
            {settlementCompanies.map((company) => (
              <button
                key={company.code}
                onClick={() => handleTabChange(company.code)}
                className={cn(
                  'h-9 min-w-max rounded-lg border px-3 text-[12px] font-black transition-colors',
                  activeTab === company.code
                    ? 'border-transparent bg-accent-50 text-accent-600 dark:bg-accent-900/20 dark:text-accent-300'
                    : 'border-[var(--border)] bg-[var(--bg-subtle)] text-[var(--text-muted)]',
                )}
              >
                {company.name}
              </button>
            ))}
          </div>
          <div className="p-3">
            {activeTab && (
              historyLoading && !historyMap[activeTab]
                ? <div className="p-4 text-[12px] font-semibold text-[var(--text-muted)]">이력을 불러오는 중입니다.</div>
                : <SettlementHistoryTimeline
                    companyCode={activeTab}
                    historyData={historyMap[activeTab] || null}
                    saving={historySaving}
                    onSave={(date, fields) => saveHistoryEntry(activeTab, date, fields)}
                    onDelete={(date) => deleteHistoryEntry(activeTab, date)}
                  />
            )}
          </div>
        </>
      )}
    </SettingsPanel>
  );
}
